import { api } from "./client";
import type { OrgRole } from "@mycompbox/shared";

/**
 * A member row as returned by the organization members routes.
 *
 * Mirrors the server's `memberSelect` exactly — the routes deliberately
 * return only these fields, so declaring anything more here would be a lie
 * the compiler could not catch.
 */
export interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  /**
   * Typed against the shared union, not `string` — the role picker and the
   * badge rendering both switch on this value, and a raw string would let a
   * typo compile.
   */
  orgRole: OrgRole;
  isActive: boolean;
  /** ISO-8601. */
  createdAt: string;
  /** The member's teams in this organization. Only GET /members returns it. */
  teams?: MemberTeam[];
}

/** A team a member belongs to, with their role on it. */
export interface MemberTeam {
  id: string;
  name: string;
  role: "admin" | "member" | "guest";
}

/** A team of the caller's organization (GET /organization/teams). */
export interface OrganizationTeam {
  id: string;
  name: string;
  parentId: string | null;
}

export interface MembersResponse {
  data: OrganizationMember[];
}

export interface MemberResponse {
  data: OrganizationMember;
}

/**
 * Client for the organization-admin member routes.
 *
 * All three calls require organization-admin authority on the server; the
 * UI additionally hides these affordances from plain members, but the server
 * is the enforcement point, not this client.
 */
export const organizationApi = {
  /** GET /api/v1/organization/members — members of the caller's org. */
  members: () => api.get<MembersResponse>("/organization/members"),

  /** GET /api/v1/organization/teams — the organization's teams. */
  teams: () => api.get<{ data: OrganizationTeam[] }>("/organization/teams"),

  /** POST /api/v1/organization/members/:id/teams — add a member to a team. */
  addMemberToTeam: (id: string, teamId: string) =>
    api.post<{ data: MemberTeam }>(`/organization/members/${id}/teams`, { teamId }),

  /** DELETE /api/v1/organization/members/:id/teams/:teamId — take a member off a team. */
  removeMemberFromTeam: (id: string, teamId: string) =>
    api.delete(`/organization/members/${id}/teams/${teamId}`),

  /**
   * PATCH /api/v1/organization/members/:id/role — change a member's role.
   *
   * The server refuses self-edits and last-admin demotions; those refusals
   * surface as ApiError with the server's message, which the UI shows as-is.
   */
  updateMemberRole: (id: string, role: OrgRole) =>
    api.patch<MemberResponse>(`/organization/members/${id}/role`, { role }),

  /**
   * DELETE /api/v1/organization/members/:id — remove a member.
   *
   * Removal DETACHES rather than deletes: the user row survives, authored
   * records keep their attribution. Self-removal and last-admin removal are
   * refused server-side.
   */
  removeMember: (id: string) => api.delete(`/organization/members/${id}`),

  /** GET /api/v1/organization/domains — claimed company email domains. */
  domains: () => api.get<{ data: OrganizationDomain[] }>("/organization/domains"),

  /** POST /api/v1/organization/domains — start a claim; returns the TXT record. */
  claimDomain: (domain: string) =>
    api.post<{ data: OrganizationDomain }>("/organization/domains", { domain }),

  /** POST /api/v1/organization/domains/:id/verify — check the DNS TXT record. */
  verifyDomain: (id: string) =>
    api.post<{ data: OrganizationDomain }>(`/organization/domains/${id}/verify`, {}),

  /** PATCH /api/v1/organization/domains/:id — toggle auto-join. */
  setDomainAutoJoin: (id: string, autoJoin: boolean) =>
    api.patch<{ data: OrganizationDomain }>(`/organization/domains/${id}`, { autoJoin }),

  /** DELETE /api/v1/organization/domains/:id */
  removeDomain: (id: string) => api.delete(`/organization/domains/${id}`),

  /** GET /api/v1/organization — the caller's organization (org-admin only). */
  get: () => api.get<{ data: { id: string; name: string } }>("/organization"),

  /** PATCH /api/v1/organization — rename (org-admin only). */
  rename: (name: string) => api.patch<{ data: { id: string; name: string } }>("/organization", { name }),

  /** GET /api/v1/organization/join-requests — pending requests, oldest first. */
  joinRequests: () => api.get<{ data: JoinRequest[] }>("/organization/join-requests"),

  /** POST /api/v1/organization/join-requests/:id/approve */
  approveJoinRequest: (id: string) => api.post(`/organization/join-requests/${id}/approve`, {}),

  /** POST /api/v1/organization/join-requests/:id/decline */
  declineJoinRequest: (id: string) => api.post(`/organization/join-requests/${id}/decline`, {}),
};

/** A pending request to join the caller's organization. */
export interface JoinRequest {
  id: string;
  /** ISO-8601. */
  createdAt: string;
  user: { id: string; name: string; email: string };
}

/**
 * A company email domain claimed by the organization. Once verified (DNS TXT)
 * with auto-join on, new people signing up with an address at this domain
 * join this organization automatically.
 */
export interface OrganizationDomain {
  id: string;
  domain: string;
  verified: boolean;
  /** ISO-8601, or null while pending. */
  verifiedAt: string | null;
  autoJoin: boolean;
  /** The DNS TXT record to publish to prove control of the domain. */
  txtRecord: { host: string; value: string };
  createdAt: string;
}
